import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  BookOpen,
  Eye,
  Edit3,
  Trash2,
  ChevronDown,
  Calendar,
  Award,
  GitBranch,
  Lock,
  AlertTriangle,
  XCircle,
  ShieldCheck,
  Clock,
  Phone,
  MessageCircle,
  UserCheck,
  Building2,
  Printer,
} from 'lucide-react';
import type { Book, User, BookStatus } from '../../../../shared/types/index.js';
import { BookStatusBadge } from './BookStatusBadge.js';
import { isOwnerOrAdmin, isAuthorSubmission, canEditBookMetadata, canDeleteBook } from './bookListUtils.js';
import { BookExpandableDetail } from '../BookExpandableDetail.js';
import { RejectReasonModal } from './RejectReasonModal.js';
import { PublisherVerificationModal } from './PublisherVerificationModal.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { resolveCoverUrl } from '../../../../shared/utils/imageCompressor.js';

interface BookTableRowProps {
  book: Book;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenModal: (book: Book) => void;
  onOpenTree: (book: Book) => void;
  onOpenCertificate: (book: Book) => void;
  onOpenPrintLabel?: (book: Book) => void;
  onEdit: (book: Book) => void;
  onDelete?: (id: string, title: string) => void;
  onUpdateStatus?: (id: string, status: BookStatus, note?: string) => void;
  isStatusPending?: boolean;
  user: User | null;
}

export const BookTableRow: React.FC<BookTableRowProps> = ({
  book,
  index,
  isExpanded,
  onToggleExpand,
  onOpenModal,
  onOpenTree,
  onOpenCertificate,
  onOpenPrintLabel,
  onEdit,
  onDelete,
  onUpdateStatus,
  isStatusPending = false,
  user,
}) => {
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectActionType, setRejectActionType] = useState<'publisher_reject' | 'admin_reject'>('publisher_reject');
  const [isPublisherVerifyModalOpen, setIsPublisherVerifyModalOpen] = useState(false);

  const isFromAuthor = isAuthorSubmission(book, user);
  const canEdit = canEditBookMetadata(book, user);

  const handleOpenRejectModal = (type: 'publisher_reject' | 'admin_reject') => {
    setRejectActionType(type);
    setIsRejectModalOpen(true);
  };

  const handleConfirmReject = (reason: string) => {
    if (!onUpdateStatus) return;
    const targetStatus: BookStatus = rejectActionType === 'publisher_reject' ? 'draft' : 'ditolak';
    onUpdateStatus(book.id, targetStatus, reason);
    setIsRejectModalOpen(false);
  };

  const handleConfirmPublisherVerification = (verificationNote: string) => {
    if (!onUpdateStatus) return;
    onUpdateStatus(book.id, 'diajukan', verificationNote);
    setIsPublisherVerifyModalOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.4), ease: 'easeOut' }}
      className={`bg-white rounded-2xl border transition overflow-hidden group ${
        isExpanded
          ? 'border-indigo-300 ring-2 ring-indigo-500/10 shadow-md'
          : 'border-slate-200/90 shadow-2xs hover:shadow-md'
      }`}
    >
      {/* Horizontal row */}
      <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Left: Cover & Book info */}
        <div className="flex gap-4 items-center min-w-0">
          <LazyBookCover
            coverUrl={resolveCoverUrl(book.coverUrl)}
            title={book.title}
            className="w-12 h-16 rounded-lg"
            targetWidth={200}
            quality={70}
            priority={index < 8}
          />

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-semibold">
                {book.category}
              </span>

              {/* Source Badge: Differentiate Penulis vs Penerbit Submission */}
              {user?.role === 'penerbit' && (
                isFromAuthor ? (
                  <span className="px-2 py-0.5 rounded-md bg-purple-100 text-purple-900 border border-purple-200 text-[10px] font-bold inline-flex items-center gap-1 shadow-2xs">
                    <UserCheck className="w-3 h-3 text-purple-600 shrink-0" />
                    <span>Diajukan Penulis</span>
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-bold inline-flex items-center gap-1">
                    <Building2 className="w-3 h-3 text-slate-500 shrink-0" />
                    <span>Pendaftaran Mandiri</span>
                  </span>
                )
              )}

              <BookStatusBadge status={book.status} />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenTree(book);
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 text-[10px] font-semibold transition border border-slate-200 cursor-pointer"
                title="Lihat Pohon Silsilah Edisi"
              >
                <GitBranch className="w-3 h-3 text-indigo-600" />
                <span>{book.edition || 'Edisi 1'}</span>
              </button>
              <span className="text-[11px] text-slate-400">
                Tahun {book.publicationYear}
              </span>
            </div>
            <h3 className="font-bold text-slate-900 text-sm truncate group-hover:text-indigo-600 transition">
              {book.title}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              Penulis: <span className="text-slate-700 font-medium">{book.authorName}</span> • Penerbit: {book.publisherName}
            </p>
          </div>
        </div>

        {/* Right: Code, Registration Time, & Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between md:justify-end gap-2.5 shrink-0 pt-3 md:pt-0 border-t md:border-t-0 border-slate-100 w-full md:w-auto">
          <div className="text-left md:text-right">
            <span className="font-mono font-bold text-xs text-slate-800 block">
              {book.uibn}
            </span>
            <span className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5 md:justify-end">
              <Calendar className="w-3 h-3 text-slate-400 shrink-0" />
              <span>Didaftarkan: {new Date(book.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 justify-end">
            <button
              type="button"
              onClick={onToggleExpand}
              className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-semibold rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs min-h-[44px] ${
                isExpanded
                  ? 'bg-indigo-600 text-white'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:bg-indigo-200'
              }`}
            >
              <span>{isExpanded ? 'Tutup' : 'Detail & Barcode'}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            <button
              type="button"
              onClick={() => onOpenModal(book)}
              className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-slate-100 active:bg-slate-200 rounded-lg transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Buka Modal Rincian"
            >
              <Eye className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => onOpenTree(book)}
              className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 active:bg-indigo-100 rounded-lg transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Pohon Silsilah Edisi (Book Edition Tree)"
            >
              <GitBranch className="w-3.5 h-3.5" />
            </button>

            {isOwnerOrAdmin(book, user) && book.status === 'disetujui' && (
              <>
                <button
                  type="button"
                  onClick={() => onOpenCertificate(book)}
                  className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 active:bg-amber-100 rounded-lg transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title="E-Sertifikat Digital (PDF)"
                >
                  <Award className="w-3.5 h-3.5" />
                </button>

                {onOpenPrintLabel && (
                  <button
                    type="button"
                    onClick={() => onOpenPrintLabel(book)}
                    className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100 rounded-lg transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title="Cetak Label & Barcode UIBN (Printer-Friendly)"
                  >
                    <Printer className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}

            {/* Edit Action Button */}
            {isOwnerOrAdmin(book, user) && (
              canEdit ? (
                <button
                  type="button"
                  onClick={() => onEdit(book)}
                  className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 active:bg-slate-200 rounded-lg transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title="Ubah Buku"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
              ) : (
                <div
                  className="p-1.5 text-slate-300 cursor-not-allowed select-none min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={
                    user?.role === 'penerbit' && isFromAuthor
                      ? 'Metadata dikelola oleh Penulis. Penerbit tidak dapat mengedit metadata langsung.'
                      : 'Metadata terkunci selama peninjauan UIBN'
                  }
                >
                  <Lock className="w-3.5 h-3.5" />
                </div>
              )
            )}

            {canDeleteBook(book, user) && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(book.id, book.title)}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-slate-100 active:bg-rose-100 rounded-lg transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Hapus Draft UIBN"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Feedback / Rejection Note banner */}
      {(book.status === 'draft' || book.status === 'ditolak' || Boolean(book.editionNotes)) && (
        <div className="px-4 pb-3 sm:px-5 sm:pb-4">
          <div
            className={`p-3 rounded-xl border text-xs space-y-2 shadow-2xs ${
              book.status === 'ditolak'
                ? 'bg-rose-50/95 border-rose-200 text-rose-950'
                : 'bg-amber-50/95 border-amber-300 text-amber-950 ring-1 ring-amber-400/20'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={`font-bold text-[11px] flex items-center gap-1.5 ${
                  book.status === 'ditolak' ? 'text-rose-800' : 'text-amber-900'
                }`}
              >
                {book.status === 'ditolak' ? (
                  <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                )}
                <span>
                  {user?.role === 'penerbit'
                    ? book.status === 'ditolak'
                      ? 'Naskah Telah Ditolak Redaksi'
                      : 'Catatan Penolakan / Revisi yang Anda Berikan:'
                    : book.status === 'ditolak'
                    ? 'Pengajuan UIBN Ditolak'
                    : book.editionNotes
                    ? 'Catatan Revisi / Masukan Penerbit:'
                    : 'Naskah Berstatus Draf (Perlu Tindakan):'}
                </span>
              </span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 ${
                  book.status === 'ditolak'
                    ? 'bg-rose-200 text-rose-800'
                    : 'bg-amber-200 text-amber-900'
                }`}
              >
                {book.status === 'ditolak' ? 'Ditolak' : 'Perlu Revisi'}
              </span>
            </div>

            <p className="text-slate-800 bg-white p-2.5 rounded-lg border border-amber-200/80 font-mono text-[11px] break-words leading-relaxed shadow-2xs">
              {book.editionNotes ||
                (user?.role === 'penerbit'
                  ? 'Naskah ini telah ditolak oleh redaksi Anda. Menunggu Penulis melakukan perbaikan metadata dan mengajukan ulang.'
                  : book.status === 'ditolak'
                  ? 'Pengajuan naskah ini ditolak oleh verifikator. Silakan tinjau kembali metadata atau perbaiki naskah sebelum mengajukan ulang.'
                  : book.publisherId
                  ? 'Naskah berada dalam status draf untuk kurasi penerbit terdaftar. Silakan lengkapi atau revisi metadata naskah lalu klik tombol Kirim Ulang ke Penerbit.'
                  : 'Naskah tersimpan sebagai draf. Hak pengeditan metadata naskah dibuka penuh.')}
            </p>

            {/* Render Edit & Resubmit buttons ONLY FOR AUTHOR */}
            {user?.role === 'penulis' && onUpdateStatus && (
              <div className="pt-2 border-t border-amber-200/70 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[10px] text-slate-600 font-medium">
                  {book.status === 'ditolak'
                    ? 'Perbaiki naskah lalu ajukan ulang verifikasi:'
                    : 'Metadata naskah terbuka untuk diperbaiki:'}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onEdit(book)}
                    className="px-2.5 py-1 bg-white hover:bg-slate-50 text-indigo-700 border border-indigo-200 rounded-md font-semibold text-[11px] flex items-center gap-1 cursor-pointer transition shadow-2xs"
                  >
                    <Edit3 className="w-3 h-3 text-indigo-600" />
                    <span>Perbaiki Naskah</span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onUpdateStatus(
                        book.id,
                        book.publisherId ? 'menunggu_penerbit' : 'diajukan'
                      )
                    }
                    disabled={isStatusPending}
                    className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-md font-bold text-[11px] flex items-center gap-1 cursor-pointer transition shadow-2xs"
                  >
                    <span>
                      {book.publisherId
                        ? 'Kirim Ulang ke Penerbit'
                        : 'Ajukan Ulang ke Admin'}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Notice for Penerbit on rejected author submission */}
            {user?.role === 'penerbit' && isFromAuthor && (
              <div className="pt-1.5 border-t border-amber-200/70 flex items-center justify-between text-[11px] text-slate-600">
                <span className="font-semibold text-rose-800">
                  Status: Menunggu Penulis melakukan perbaikan metadata.
                </span>
                {book.authorWhatsapp && (
                  <a
                    href={`https://wa.me/${book.authorWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 font-bold hover:underline flex items-center gap-1"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Hubungi Penulis</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Publisher Endorsement / Curation Bar */}
      {user?.role === 'penerbit' && book.status === 'menunggu_penerbit' && book.publisherId === user.id && (
        <div className="px-4 pb-3 sm:px-5 sm:pb-4">
          <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200 text-xs space-y-2.5 shadow-2xs">
            <div className="flex items-center justify-between text-indigo-900 font-bold">
              <span className="flex items-center gap-1.5 text-xs">
                <Clock className="w-3.5 h-3.5 text-indigo-600 animate-pulse" />
                <span>Konfirmasi & Kurasi Penerbitan Naskah Masuk</span>
              </span>
            </div>

            {book.authorWhatsapp && (
              <div className="p-2 rounded-lg bg-white border border-indigo-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span className="text-[11px] text-slate-700 truncate font-medium">
                    WA Penulis: <strong className="font-mono text-slate-900">{book.authorWhatsapp}</strong>
                  </span>
                </div>
                <a
                  href={`https://wa.me/${book.authorWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold flex items-center gap-1 shrink-0"
                >
                  <MessageCircle className="w-3 h-3" />
                  <span>Chat WA</span>
                </a>
              </div>
            )}

            <div className="flex items-center gap-1.5 pt-1 border-t border-indigo-200/60">
              <button
                type="button"
                onClick={() => setIsPublisherVerifyModalOpen(true)}
                disabled={isStatusPending}
                className="flex-1 py-1.5 px-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold rounded-lg transition text-[11px] flex items-center justify-center gap-1 cursor-pointer shadow-2xs"
                title="Verifikasi syarat kelayakan naskah sebelum dikirim ke Admin UIBN"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Verifikasi</span>
              </button>
              <button
                type="button"
                onClick={() => handleOpenRejectModal('publisher_reject')}
                disabled={isStatusPending}
                className="flex-1 py-1.5 px-2 bg-slate-500 hover:bg-slate-600 active:bg-slate-700 text-white font-bold rounded-lg transition text-[11px] flex items-center justify-center gap-1 cursor-pointer shadow-2xs"
                title="Tolak naskah atau minta revisi ke penulis"
              >
                <XCircle className="w-3.5 h-3.5" />
                <span>Tolak/Revisi</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline Expandable Detail View */}
      {isExpanded && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
          <BookExpandableDetail
            book={book}
            onOpenModal={onOpenModal}
            onEdit={onEdit}
            onCollapse={onToggleExpand}
          />
        </div>
      )}

      {/* Reject / Revision Note Modal */}
      <RejectReasonModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        bookTitle={book.title}
        actionType={rejectActionType}
        isPending={isStatusPending}
      />

      {/* Publisher Verification Checklist Modal */}
      <PublisherVerificationModal
        isOpen={isPublisherVerifyModalOpen}
        onClose={() => setIsPublisherVerifyModalOpen(false)}
        onConfirm={handleConfirmPublisherVerification}
        book={book}
        isPending={isStatusPending}
      />
    </motion.div>
  );
};
